import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { money, dateTime, fmtDob } from '../lib/data';
import { useEligibility, usePatients, usePractices } from '../api/hooks';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import StatusPill from '../components/StatusPill';
import RiskBadge from '../components/RiskBadge';
import EmptyState from '../components/EmptyState';
import { Plus, Search, ShieldCheck, ChevronRight } from 'lucide-react';

const FILTERS = [
  { k: 'all', label: 'All' },
  { k: 'in_network', label: 'In-network' },
  { k: 'out_of_network', label: 'Out-of-network' },
  { k: 'unknown', label: 'Unknown' },
  { k: 'not_in_db', label: 'Not in DB' },
];

export default function Eligibility() {
  const { session } = useAuth();
  const nav = useNavigate();
  const practiceId = session?.practice?.id || 'prac_001';
  const scopeAll = practiceId === 'all';
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [payer, setPayer] = useState('all');

  const { data: inPractice = [] } = useEligibility(practiceId);
  const { data: patients = [] } = usePatients(practiceId);
  const { data: practices = [] } = usePractices();

  const dobByPatient = useMemo(() => new Map(patients.map((p) => [p.id, p.dob])), [patients]);
  const practiceNameById = useMemo(() => new Map(practices.map((p) => [p.id, p.name])), [practices]);
  const payers = useMemo(() => Array.from(new Map(inPractice.map((c) => [c.payerId, { id: c.payerId, name: c.payerName }]).filter(([id]) => id)).values()), [inPractice]);

  const checks = useMemo(() => {
    return inPractice
      .filter((c) => filter === 'all' || c.networkStatusResolved === filter)
      .filter((c) => payer === 'all' || c.payerId === payer)
      .filter((c) => {
        if (!q) return true;
        return `${c.patientName || ''} ${c.planNameReturned}`.toLowerCase().includes(q.toLowerCase());
      })
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }, [inPractice, q, filter, payer]);

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Eligibility AI" title="Verifications" subtitle="Every 270/271 eligibility check run for your practice.">
        <Link to="/app/eligibility/new"><Button className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Run check</Button></Link>
      </PageHeader>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-9 bg-white" placeholder="Search patient or plan…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Select value={payer} onValueChange={setPayer}><SelectTrigger className="h-9 w-auto min-w-[130px] bg-white text-sm"><SelectValue placeholder="Payer" /></SelectTrigger><SelectContent><SelectItem value="all">All payers</SelectItem>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          <div className="seg">{FILTERS.map((f) => <button key={f.k} className={filter === f.k ? 'active' : ''} onClick={() => setFilter(f.k)}>{f.label}</button>)}</div>
        </div>
      </div>

      <div className="dh-card-flush overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50/70">
              <tr>
                {['Submitted', ...(scopeAll ? ['Practice'] : []), 'Patient', 'Plan returned', 'Network', 'Risk', 'Copay', 'Deductible (met)', ''].map((h, i) => (
                  <th key={i} className={`font-medium px-4 py-2.5 ${['Copay', 'Deductible (met)'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {checks.map((c) => {
                const risk = c.risk || {};
                return (
                  <tr key={c.id} className="border-t border-slate-100 row-hover cursor-pointer" onClick={() => nav(`/app/eligibility/${c.id}`)}>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium">{dateTime(c.submittedAt)}</div>
                      <div className="text-[11px] text-slate-400 mono">{c.id.slice(0, 16)}…</div>
                    </td>
                    {scopeAll && <td className="px-4 py-3 text-slate-600 text-xs">{practiceNameById.get(c.practiceId)}</td>}
                    <td className="px-4 py-3"><div className="font-medium text-slate-900">{c.patientName}</div><div className="text-xs text-slate-500">DOB {fmtDob(dobByPatient.get(c.patientId))}</div></td>
                    <td className="px-4 py-3"><div className="text-slate-900">{c.planNameReturned}</div><div className="text-xs text-slate-500">{c.payerName}</div></td>
                    <td className="px-4 py-3"><StatusPill status={c.networkStatusResolved} /></td>
                    <td className="px-4 py-3"><RiskBadge level={risk.level} score={risk.score} showScore /></td>
                    <td className="px-4 py-3 text-right text-slate-700 tnum">{money(c.financials.copay)}</td>
                    <td className="px-4 py-3 text-right text-slate-700 tnum">{money(c.financials.deductibleIndMet)} / {money(c.financials.deductibleInd)}</td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {checks.length === 0 && <EmptyState icon={ShieldCheck} title="No verifications match" hint="Try a different filter or run a new eligibility check." />}
      </div>
    </div>
  );
}
